import { useRef, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { useAuth } from '../context/AuthContext'
import { useSnackbar } from '../context/snackbar-context'
import usersApi from '../client/users-api'

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp']
const MAX_BYTES = 3 * 1024 * 1024

const Preview = styled('img')(({ theme }) => ({
  width: 140,
  height: 140,
  borderRadius: '50%',
  objectFit: 'cover',
  border: `1px solid ${theme.palette.divider}`,
}))

const PreviewPlaceholder = styled(Box)(({ theme }) => ({
  width: 140,
  height: 140,
  borderRadius: '50%',
  border: `1px dashed ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.text.secondary,
  fontSize: 13,
  textAlign: 'center',
  padding: theme.spacing(1),
}))

const Center = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(2),
  paddingTop: theme.spacing(1),
}))

type Props = {
  open: boolean
  onClose: () => void
}

const AvatarUploadModal = ({ open, onClose }: Props) => {
  const { getAccessToken, refreshUser } = useAuth()
  const { enqueueSnackbar } = useSnackbar()
  const inputRef = useRef<HTMLInputElement>(null)

  const [file, setFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const reset = () => {
    setFile(null)
    setPreviewUrl(null)
    setSaving(false)
  }

  const handleClose = () => {
    if (saving) return
    reset()
    onClose()
  }

  const handlePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0]
    if (!picked) return

    if (!ALLOWED_TYPES.includes(picked.type)) {
      enqueueSnackbar('Only JPEG, PNG, or WebP images are allowed', {
        variant: 'error',
      })
      return
    }
    if (picked.size > MAX_BYTES) {
      enqueueSnackbar('Image must be 3 MB or smaller', { variant: 'error' })
      return
    }

    setFile(picked)
    setPreviewUrl(URL.createObjectURL(picked))
  }

  const handleSave = async () => {
    if (!file) return
    setSaving(true)
    try {
      const token = await getAccessToken()
      const formData = new FormData()
      // Field name must match the controller's IFormFile parameter (`file`).
      formData.append('file', file)
      await usersApi.updateAvatar(formData, token)
      await refreshUser()
      enqueueSnackbar('Avatar updated', { variant: 'success' })
      handleClose()
    } catch (err) {
      console.log(err)
      enqueueSnackbar('Failed to update avatar', { variant: 'error' })
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='xs' fullWidth>
      <DialogTitle>Update avatar</DialogTitle>
      <DialogContent>
        <Center>
          {previewUrl ? (
            <Preview src={previewUrl} alt='Preview' />
          ) : (
            <PreviewPlaceholder>No image selected</PreviewPlaceholder>
          )}

          <input
            ref={inputRef}
            type='file'
            accept='image/jpeg,image/png,image/webp'
            hidden
            onChange={handlePick}
          />
          <Button
            variant='outlined'
            onClick={() => inputRef.current?.click()}
            disabled={saving}
          >
            Choose image
          </Button>
          <Typography variant='caption' color='text.secondary'>
            JPEG, PNG or WebP, up to 3 MB.
          </Typography>
        </Center>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant='contained'
          onClick={handleSave}
          disabled={!file || saving}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AvatarUploadModal
